import { useState, useRef, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { streamChat, type ChatMessage, type SessionContact } from "@/lib/chatClient";
import {
  type FlowState,
  type FlowType,
  INITIAL_FLOW_STATE,
  isFlowActive,
  startFlow,
  advanceFlow,
} from "@/lib/supplierFlowController";
import { ChatInput, type ChatAttachment } from "@/components/ChatInput";
import { ChatDisclaimer } from "@/components/ChatDisclaimer";
import { SupplierChatSidebar } from "@/components/SupplierChatSidebar";
import { MapPin, Briefcase, Menu, Send, Info } from "lucide-react";
import ReactMarkdown from "react-markdown";
import { fileToBase64 } from "@/lib/chatClient";
import { useSupplierMeta } from "@/hooks/useSupplierMeta";
import { parseFaq, generateCanonicalUrl } from "@/lib/seoHelpers";
import { useI18n } from "@/lib/i18n";
import { toast } from "sonner";
import { hasCompletedSupplierInfo } from "@/lib/supplierReadiness";

interface Supplier {
  id: string;
  company_name: string;
  description: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  region: string | null;
  slug: string | null;
  custom_domain: string | null;
  ico: string | null;
  page_title: string | null;
  meta_description: string | null;
  og_image_url: string | null;
  business_type: string | null;
  service_area: string | null;
  public_tagline: string | null;
  enabled_services: string[];
  bot_name: string | null;
  bot_intro: string | null;
  bot_bio: string | null;
  bot_faq: string | null;
}

interface Message {
  id: string;
  role: "user" | "assistant";
  content: string;
}

function stripLeadBlock(text: string) {
  return text.replace(/---LEAD_DATA---[\s\S]*?---END_LEAD---/g, "").trim();
}

function stripDirectMsgBlock(text: string) {
  return text.replace(/---DIRECT_MSG---[\s\S]*?---END_MSG---/g, "").trim();
}

function stripInquiryBlock(text: string) {
  return text.replace(/---INQUIRY_DATA---[\s\S]*?---END_INQUIRY---/g, "").trim();
}

function extractDirectMsg(text: string): { email: string; message: string } | null {
  const match = text.match(/---DIRECT_MSG---\s*(\{[\s\S]*?\})\s*---END_MSG---/);
  if (!match) return null;
  try {
    return JSON.parse(match[1]);
  } catch {
    return null;
  }
}

function extractInquiryData(text: string): { email: string; phone?: string | null; description: string } | null {
  const match = text.match(/---INQUIRY_DATA---\s*(\{[\s\S]*?\})\s*---END_INQUIRY---/);
  if (!match) return null;
  try {
    return JSON.parse(match[1]);
  } catch {
    return null;
  }
}

interface SupplierChatPageProps {
  domain?: string;
}

export default function SupplierChatPage({ domain }: SupplierChatPageProps = {}) {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { t } = useI18n();
  const isCustomDomain = !!domain;

  const [supplier, setSupplier] = useState<Supplier | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [supplierReady, setSupplierReady] = useState(true);

  const [publicFeatures, setPublicFeatures] = useState<Record<string, string>>({});
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [streaming, setStreaming] = useState(false);
  const streamAbortRef = useRef<AbortController | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const sessionIdRef = useRef(crypto.randomUUID());
  const sentDirectMsgs = useRef<Set<string>>(new Set());
  const sessionContactRef = useRef<SessionContact>({});
  const flowStateRef = useRef<FlowState>(INITIAL_FLOW_STATE);

  // Sidebar state
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Dynamic SEO meta tags
  useSupplierMeta(supplier, isCustomDomain, domain);

  useEffect(() => {
    return () => { streamAbortRef.current?.abort(); };
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, streaming]);

  useEffect(() => {
    if (domain) {
      loadSupplierByDomain(domain);
    } else if (slug) {
      loadSupplierBySlug(slug);
    } else {
      setNotFound(true);
      setLoading(false);
    }
  }, [slug, domain]);

  // Load public feature entitlements for supplier
  useEffect(() => {
    if (!supplier?.id) return;
    supabase.rpc("get_supplier_public_features", { p_supplier_id: supplier.id })
      .then(({ data }) => {
        if (data) {
          const map: Record<string, string> = {};
          (data as { feature_key: string; status: string }[]).forEach(r => { map[r.feature_key] = r.status; });
          setPublicFeatures(map);
        }
      });
  }, [supplier?.id]);

  const checkReadiness = async (s: Supplier) => {
    if (!hasCompletedSupplierInfo(s)) {
      setSupplierReady(false);
      return;
    }
    const { count } = await supabase
      .from("supplier_price_items")
      .select("id", { count: "exact", head: true })
      .eq("supplier_id", s.id)
      .eq("is_active", true);
    if ((count ?? 0) === 0) {
      setSupplierReady(false);
    }
  };

  const loadSupplierBySlug = async (s: string) => {
    const { data, error } = await supabase.rpc("get_supplier_by_slug", { p_slug: s });
    if (!error && data && data.length > 0) {
      const sup = data[0] as Supplier;
      setSupplier(sup);
      await checkReadiness(sup);
    } else {
      setNotFound(true);
    }
    setLoading(false);
  };

  const loadSupplierByDomain = async (d: string) => {
    const { data, error } = await supabase.rpc("get_supplier_by_domain", { p_domain: d });
    if (!error && data && data.length > 0) {
      const sup = data[0] as Supplier;
      setSupplier(sup);
      await checkReadiness(sup);
    } else {
      setNotFound(true);
    }
    setLoading(false);
  };

  const saveDirectMessage = async (email: string, message: string, msgId: string) => {
    if (!supplier || sentDirectMsgs.current.has(msgId)) return;
    sentDirectMsgs.current.add(msgId);
    try {
      const { error: insertError } = await supabase.from("supplier_customer_messages").insert({
        supplier_id: supplier.id,
        customer_email: email,
        message,
      });

      if (insertError) throw insertError;

      const { error: notifyError } = await supabase.functions.invoke("notify-supplier-message", {
        body: {
          supplier_id: supplier.id,
          customer_email: email,
          message,
        },
      });

      if (notifyError) {
        console.error("notify-supplier-message failed:", notifyError);
        toast.error("Zpráva se uložila, ale upozornění firmě se nepodařilo odeslat.");
      }
    } catch (error) {
      sentDirectMsgs.current.delete(msgId);
      console.error("saveDirectMessage failed:", error);
      toast.error("Zprávu se nepodařilo doručit firmě. Zkuste to prosím znovu.");
    }
  };

  const saveInquiry = async (data: { email: string; phone?: string | null; description: string }, msgId: string) => {
    if (!supplier || sentDirectMsgs.current.has(msgId)) return;
    sentDirectMsgs.current.add(msgId);
    try {
      const { error } = await supabase.functions.invoke("create-supplier-inquiry", {
        body: {
          supplier_id: supplier.id,
          email: data.email,
          phone: data.phone || null,
          description: data.description,
          service_slug: supplier.enabled_services?.[0] || null,
        },
      });

      if (error) throw error;
    } catch (error) {
      sentDirectMsgs.current.delete(msgId);
      console.error("saveInquiry failed:", error);
      toast.error("Poptávku se nepodařilo doručit firmě. Zkuste to prosím znovu.");
    }
  };

  const getDisplayName = () => {
    if (!supplier) return "";
    return supplier.company_name?.includes("@")
      ? supplier.company_name.split("@")[0]
      : supplier.company_name ?? "";
  };

  const handleStartFlow = (type: FlowType) => {
    if (!supplier) return;
    // Reset any in-progress flow before starting a new one
    flowStateRef.current = INITIAL_FLOW_STATE;

    const companyName = getDisplayName();
    const result = startFlow(type, sessionContactRef.current, companyName);
    flowStateRef.current = result.nextState;

    const assistantId = crypto.randomUUID();
    setMessages((prev) => [
      ...prev,
      { id: assistantId, role: "assistant", content: result.assistantMessage },
    ]);
  };

  const sendMessage = async (text: string, attachments?: ChatAttachment[]) => {
    if (!text.trim() || streaming || !supplier) return;

    // --- Flow interception: route through state machine instead of AI ---
    if (isFlowActive(flowStateRef.current)) {
      const companyName = getDisplayName();
      const userMsg: Message = { id: crypto.randomUUID(), role: "user", content: text };
      const assistantId = crypto.randomUUID();

      const result = advanceFlow(
        flowStateRef.current,
        text,
        sessionContactRef.current,
        companyName,
      );
      flowStateRef.current = result.nextState;

      // Save email/phone to session contact immediately
      if (result.detectedEmail) {
        sessionContactRef.current = { ...sessionContactRef.current, email: result.detectedEmail };
      }
      if (result.detectedPhone) {
        sessionContactRef.current = { ...sessionContactRef.current, phone: result.detectedPhone };
      }

      setMessages((prev) => [
        ...prev,
        userMsg,
        { id: assistantId, role: "assistant", content: result.assistantMessage },
      ]);
      setInputValue("");

      // Handle completed flow
      if (result.action) {
        if (result.action.type === "direct_msg") {
          saveDirectMessage(result.action.email, result.action.message, assistantId);
        } else if (result.action.type === "inquiry") {
          saveInquiry(
            { email: result.action.email, phone: result.action.phone, description: result.action.description },
            `inq-${assistantId}`,
          );
        }
        flowStateRef.current = INITIAL_FLOW_STATE;
      }

      return;
    }

    streamAbortRef.current?.abort();
    const controller = new AbortController();
    streamAbortRef.current = controller;

    const userMsg: Message = { id: crypto.randomUUID(), role: "user", content: text };
    const assistantId = crypto.randomUUID();
    setMessages((prev) => [...prev, userMsg, { id: assistantId, role: "assistant", content: "" }]);
    setInputValue("");
    setStreaming(true);

    const apiMessages: ChatMessage[] = [
      ...messages.map((m) => ({ role: m.role as "user" | "assistant", content: m.content })),
    ];

    if (attachments && attachments.length > 0) {
      const parts: Array<{ type: "text"; text: string } | { type: "image_url"; image_url: { url: string } }> = [
        { type: "text", text },
      ];
      for (const att of attachments) {
        if (att.file) {
          const b64 = await fileToBase64(att.file);
          parts.push({ type: "image_url", image_url: { url: b64 } });
        }
      }
      apiMessages.push({ role: "user", content: parts });
    } else {
      apiMessages.push({ role: "user", content: text });
    }

    await streamChat({
      messages: apiMessages,
      supplierId: supplier.id,
      sessionId: sessionIdRef.current,
      sessionContact: sessionContactRef.current,
      engine: supplier.enabled_services?.[0] || null,
      signal: controller.signal,
      onDelta: (delta) => {
        setMessages((prev) =>
          prev.map((m) => m.id === assistantId ? { ...m, content: m.content + delta } : m)
        );
      },
      onDone: () => {
        setStreaming(false);
        setMessages((prev) => {
          const msg = prev.find((m) => m.id === assistantId);
          if (msg) {
            const dm = extractDirectMsg(msg.content);
            if (dm?.email && dm?.message) {
              saveDirectMessage(dm.email, dm.message, assistantId);
              // Store email in session contact memory for reuse
              sessionContactRef.current = { ...sessionContactRef.current, email: dm.email };
            }
            const inq = extractInquiryData(msg.content);
            if (inq?.email && inq?.description) {
              saveInquiry(inq, `inq-${assistantId}`);
              // Store email (and phone if present) in session contact memory
              sessionContactRef.current = {
                ...sessionContactRef.current,
                email: inq.email,
                ...(inq.phone ? { phone: inq.phone } : {}),
              };
            }
          }
          return prev;
        });
      },
      onError: (err) => {
        setMessages((prev) =>
          prev.map((m) => m.id === assistantId ? { ...m, content: `Chyba: ${err}` } : m)
        );
        setStreaming(false);
      },
    });
  };

  // Sidebar actions
  const handleNewChat = () => {
    setMessages([]);
    setInputValue("");
    sessionIdRef.current = crypto.randomUUID();
    sentDirectMsgs.current.clear();
    sessionContactRef.current = {};
    flowStateRef.current = INITIAL_FLOW_STATE;
  };

  const handleShare = async () => {
    if (!supplier) return;
    const url = generateCanonicalUrl(supplier, isCustomDomain, domain || window.location.hostname);
    if (navigator.share) {
      try {
        await navigator.share({ title: getDisplayName(), url });
        return;
      } catch (e) {
        if ((e as Error).name === "AbortError") return;
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success(t("supplier.linkCopied"));
    } catch {
      toast.error(t("supplier.shareFailed"));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground text-sm">...</div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background gap-4 p-8 text-center">
        <p className="text-2xl font-semibold text-foreground">AI chat nenalezen</p>
        <p className="text-muted-foreground text-sm">
          {isCustomDomain
            ? "Tato doména zatím není propojena s žádným ceníkem."
            : <>Adresa <span className="font-mono">{slug}</span> neexistuje.</>
          }
        </p>
        {!isCustomDomain && (
          <button onClick={() => navigate("/")} className="text-sm text-primary underline">← Zpět na hlavní stránku</button>
        )}
      </div>
    );
  }

  if (!supplierReady) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background gap-4 p-8 text-center">
        <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mb-2">
          <Briefcase className="w-6 h-6 text-muted-foreground" />
        </div>
        <p className="text-xl font-semibold text-foreground">{t("supplier.readiness.fallbackTitle")}</p>
        <p className="text-muted-foreground text-sm max-w-md">{t("supplier.readiness.fallbackDescription")}</p>
        {!isCustomDomain && (
          <button onClick={() => navigate("/")} className="text-sm text-primary underline mt-2">← cenik.ai</button>
        )}
      </div>
    );
  }

  /** Strip @domain from company_name if it looks like an email */
  const displayName = supplier?.company_name?.includes("@")
    ? supplier.company_name.split("@")[0]
    : supplier?.company_name ?? "";

  const hasMessages = messages.length > 0;
  const faqItems = parseFaq(supplier?.bot_faq);

  return (
    <div className="flex h-[100dvh] bg-background overflow-hidden">
      {/* Sidebar */}
      {supplier && (
        <SupplierChatSidebar
          isOpen={sidebarOpen}
          onToggle={() => setSidebarOpen(!sidebarOpen)}
          supplierName={displayName}
          onNewChat={handleNewChat}
          onInquiry={() => handleStartFlow("inquiry")}
          onMessage={() => handleStartFlow("message")}
          onAbout={() => sendMessage(t("supplier.aboutQuery"))}
          publicFeatures={publicFeatures}
        />
      )}

      {/* Main content area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar with menu toggle */}
        <div className="h-12 flex items-center px-3 flex-shrink-0">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 rounded-xl text-muted-foreground hover:bg-muted/50 transition-colors"
          >
            <Menu className="w-5 h-5" />
          </button>
          {supplier && (
            <span className="ml-2 text-sm font-medium text-foreground truncate">
              {displayName}
            </span>
          )}
          <div className="ml-auto flex items-center gap-2">
            <button
              onClick={handleShare}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-border text-foreground rounded-full hover:bg-muted/50 transition-colors"
              aria-label={t("supplier.share")}
            >
              <Send className="w-3.5 h-3.5" />
              <span>{t("supplier.share")}</span>
            </button>
            <button
              onClick={() => sendMessage(t("supplier.aboutQuery"))}
              className="w-8 h-8 flex items-center justify-center rounded-full border border-border text-muted-foreground hover:bg-muted/50 transition-colors"
              aria-label={t("supplier.aboutCompany")}
              title={t("supplier.aboutCompany")}
            >
              <Info className="w-4 h-4" />
            </button>
          </div>
        </div>

        {!hasMessages ? (
          <>
            {/* Centered hero — matches main chat layout */}
            <div className="flex-1 flex flex-col items-center justify-center px-4">
              <h1 className="text-xl sm:text-3xl md:text-[28px] font-semibold text-foreground text-center mb-6 tracking-tight">
                {displayName}
              </h1>
              {(supplier?.public_tagline || supplier?.business_type) && (
                <p className="text-sm text-muted-foreground text-center mb-4">
                  {supplier.public_tagline || supplier.business_type}
                </p>
              )}
              <div className="w-full max-w-[680px]">
                <ChatInput
                  value={inputValue}
                  onSend={sendMessage}
                  disabled={streaming}
                  placeholder="Napište dotaz nebo parametry fasády…"
                />
              </div>
            </div>

            <div className="pb-4 px-4">
              <ChatDisclaimer />
            </div>

            {/* SEO crawlable content — hidden visually but in DOM for indexing */}
            {supplier && (
              <div className="h-0 overflow-hidden" aria-hidden="true">
                <div className="max-w-[640px] mx-auto px-4 space-y-8">
                  {/* About section */}
                  {(supplier.description || supplier.bot_bio) && (
                    <section>
                      <h2 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-1.5">
                        <Briefcase className="w-4 h-4 text-muted-foreground" />
                        O firmě {displayName}
                      </h2>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {supplier.description || supplier.bot_bio}
                      </p>
                    </section>
                  )}

                  {/* Business type + service area */}
                  {(supplier.business_type || supplier.service_area || supplier.region) && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {supplier.business_type && (
                        <section className="border border-border rounded-xl p-3">
                          <h3 className="text-xs font-medium text-muted-foreground mb-1">Specializace</h3>
                          <p className="text-sm text-foreground">{supplier.business_type}</p>
                        </section>
                      )}
                      {(supplier.service_area || supplier.region) && (
                        <section className="border border-border rounded-xl p-3">
                          <h3 className="text-xs font-medium text-muted-foreground mb-1 flex items-center gap-1">
                            <MapPin className="w-3 h-3" /> Oblast působnosti
                          </h3>
                          <p className="text-sm text-foreground">{supplier.service_area || supplier.region}</p>
                        </section>
                      )}
                    </div>
                  )}

                  {/* FAQ from bot_faq */}
                  {faqItems.length > 0 && (
                    <section>
                      <h3 className="text-sm font-semibold text-foreground mb-3">Časté dotazy</h3>
                      <div className="space-y-2">
                        {faqItems.map((item, i) => (
                          <details key={i} className="group border border-border rounded-xl overflow-hidden">
                            <summary className="px-4 py-3 text-sm font-medium text-foreground cursor-pointer hover:bg-muted/30 transition-colors">
                              {item.question}
                            </summary>
                            <div className="px-4 pb-3 text-sm text-muted-foreground leading-relaxed">
                              {item.answer}
                            </div>
                          </details>
                        ))}
                      </div>
                    </section>
                  )}
                </div>
              </div>
            )}
          </>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto scrollbar-none">
              <div className="max-w-3xl mx-auto w-full px-4 py-6 space-y-6">
                {messages.map((msg) => (
                  <div key={msg.id} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                    <div
                      className={`max-w-[80%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${
                        msg.role === "user"
                          ? "bg-primary text-primary-foreground"
                          : "bg-secondary text-secondary-foreground"
                      }`}
                    >
                      {msg.role === "assistant" ? (
                        <div className="prose prose-sm dark:prose-invert max-w-none">
                          <ReactMarkdown>{stripInquiryBlock(stripDirectMsgBlock(stripLeadBlock(msg.content))) || ""}</ReactMarkdown>
                        </div>
                      ) : (
                        <p className="whitespace-pre-wrap">{msg.content}</p>
                      )}
                    </div>
                  </div>
                ))}
                {streaming && messages[messages.length - 1]?.role === "assistant" && messages[messages.length - 1]?.content === "" && (
                  <div className="flex justify-start">
                    <div className="bg-secondary text-secondary-foreground rounded-2xl px-4 py-3 text-sm">
                      <span className="flex gap-1">
                        <span className="w-1.5 h-1.5 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                        <span className="w-1.5 h-1.5 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                        <span className="w-1.5 h-1.5 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                      </span>
                    </div>
                  </div>
                )}
                <div ref={scrollRef} />
              </div>
            </div>
            <div className="pb-4 pt-2">
              <div className="max-w-[680px] mx-auto">
                <ChatInput
                  value={inputValue}
                  onSend={sendMessage}
                  disabled={streaming}
                  placeholder="Napište dotaz nebo parametry fasády…"
                />
              </div>
              <ChatDisclaimer className="mt-2 px-4" />
            </div>
          </>
        )}
      </div>

    </div>
  );
}
